import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, Home, Pencil, BookOpen } from "lucide-react";
import { listUnits, listProperties, listEntities, listTenants, createUnit, updateUnit, deleteUnit, aktuelleMieten } from "@/lib/api";
import { UNIT_TYPES, UNIT_STATUSES, HAUPT_TYPES, ZIMMER_TYP, VERMIETUNGSARTEN, statusLabel, istAktivesMietverhaeltnis, euro, grundbuchblatt } from "@/lib/format";
import { buildLageMap, propertyDisplay } from "@/lib/propertyLabels";
import { useSchreibschutz } from "@/lib/auth";
import { sperre } from "@/hilfe/Knopfhilfe";
import { Hilfe } from "@/hilfe/Hilfe";
import StatusBadge from "@/components/StatusBadge";
import DeleteConfirm from "@/components/DeleteConfirm";
import LifecycleBadge from "@/components/LifecycleBadge";
import TaskList from "@/components/TaskList";
import { LIFECYCLE_STRATEGIES, LIFECYCLE_STATUSES } from "@/lib/tracker";
import { useTableSort, SortHeader, sortOptions } from "@/components/SortHeader";
import { SearchBox, useFulltextFilter, MatchCount } from "@/components/SearchBox";
import SuchAuswahl from "@/components/ui/such-auswahl";
import { toast } from "sonner";

const empty = {
    property_id: "", unit_label: "", typ: "wohnung", status: "leer",
    rooms: "", size_sqm: "", ausstattung: "",
    kategorie_freitext: "",
    branche: "", mwst_pflicht: false,
    stellplatz_nummer: "", eigenes_grundbuch: true, grundbuch_eintrag: "",
    nutzungsart: "", separat_vermietet: true, parent_unit_id: "", vermietungsart: "",
    // Leerstandsbeginn und die vier Besitz-/Eigentumsdaten (05.10.2026);
    // ``mietenden`` traegt beim Wechsel auf "leer" das Mietende je laufendem
    // Mietverhaeltnis -- das System erfindet kein Datum mehr.
    leer_seit: "",
    mietenden: [],
    notes: "",
};

const isHaupt = (typ) => typ === "wohnung" || typ === "gewerbe";
// Ein Zimmer ist die dritte Kategorie neben Haupteinheit und Zubehoer: Es
// haengt wie Zubehoer an einer Wohnung, traegt aber einen eigenen Mietvertrag.
// Waere es schlicht "nicht haupt", bekaeme es ueber die Zweige unten die
// Nutzungsart "Sondernutzungsrecht" statt "Einzelzimmer" und die Felder fuer
// Zubehoer -- beides falsch.
const isZimmer = (typ) => typ === ZIMMER_TYP;
const isZubehoer = (typ) => !isHaupt(typ) && !isZimmer(typ);

const Units = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const propertyFilter = searchParams.get("property_id") || "";
    const entityFilter = searchParams.get("entity_id") || "";
    const urlStrategy = searchParams.get("lifecycle_strategy") || "";
    const urlLifecycleStatus = searchParams.get("lifecycle_status") || "";
    // Belegungsstatus aus der URL, damit "Einheiten zeigen" im Datenlücken-Hinweis
    // des Cockpits direkt auf die betroffenen Einheiten führt statt auf die
    // ungefilterte Liste.
    const urlStatus = searchParams.get("status") || "";
    // Der Typ kam bisher nur aus dem Dropdown. Damit die Kennzahlen des
    // Dashboards hierher verlinken koennen ("Gewerbe 7" -> die sieben
    // Gewerbeeinheiten), muss er auch aus der Adresse lesbar sein.
    const urlTyp = searchParams.get("typ") || "";
    const [items, setItems] = useState([]);
    const [properties, setProperties] = useState([]);
    const [entities, setEntities] = useState([]);
    const [allUnits, setAllUnits] = useState([]); // for parent-selection
    const [tenants, setTenants] = useState([]);
    // Wirksame Mietanpassungen je Mietverhaeltnis (siehe load()).
    const [mieten, setMieten] = useState({});
    const [open, setOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(empty);
    const [saving, setSaving] = useState(false);
    const { gesperrt, grund } = useSchreibschutz();
    const [typFilter, setTypFilter] = useState(urlTyp);
    const [statusFilter, setStatusFilter] = useState(urlStatus);
    const [strategyFilter, setStrategyFilter] = useState(urlStrategy);
    const [lifecycleStatusFilter, setLifecycleStatusFilter] = useState(urlLifecycleStatus);
    const [expandedTasks, setExpandedTasks] = useState({});  // unitId → bool

    // URL ↔ State synchronisieren
    useEffect(() => { setStrategyFilter(urlStrategy); }, [urlStrategy]);
    useEffect(() => { setLifecycleStatusFilter(urlLifecycleStatus); }, [urlLifecycleStatus]);
    useEffect(() => { setStatusFilter(urlStatus); }, [urlStatus]);

    const load = useCallback(async () => {
        const params = {};
        if (propertyFilter) params.property_id = propertyFilter;
        if (typFilter) params.typ = typFilter;
        if (statusFilter) params.status = statusFilter;
        if (strategyFilter) params.lifecycle_strategy = strategyFilter;
        if (lifecycleStatusFilter) params.lifecycle_status = lifecycleStatusFilter;
        // Die heute gueltige Miete kommt aus den Anpassungen, nicht aus dem
        // Mieterdatensatz -- sonst steht hier die Vertragsmiete, waehrend das
        // Mietkonto laengst mit der Erhoehung rechnet. Faellt der Abruf aus,
        // bleibt die Liste stehen und zeigt den Vertragswert.
        const [list, props, ents, all, tens, m] = await Promise.all([
            listUnits(params), listProperties(), listEntities(), listUnits({}), listTenants(),
            aktuelleMieten().catch(() => ({ mieten: {} })),
        ]);
        setItems(list); setProperties(props); setEntities(ents); setAllUnits(all); setTenants(tens);
        setMieten(m.mieten || {});
    }, [propertyFilter, typFilter, statusFilter, strategyFilter, lifecycleStatusFilter]);

    useEffect(() => { load(); }, [load]);

    const propMap = useMemo(() => Object.fromEntries(properties.map((p) => [p.id, p])), [properties]);
    const entMap = useMemo(() => Object.fromEntries(entities.map((e) => [e.id, e])), [entities]);
    const lageMap = useMemo(() => buildLageMap(properties, allUnits), [properties, allUnits]);
    const sortedEntities = useMemo(() => sortOptions(entities, (e) => e.name || ""), [entities]);
    // Property-Dropdown: nur Properties der gewählten Entität anzeigen
    const filteredProperties = useMemo(
        () => entityFilter ? properties.filter((p) => p.entity_id === entityFilter) : properties,
        [properties, entityFilter]
    );
    const sortedProperties = useMemo(() => sortOptions(filteredProperties, (p) => propertyDisplay(p, lageMap)), [filteredProperties, lageMap]);
    const sortedUnitTypes = useMemo(() => sortOptions(UNIT_TYPES), []);
    const sortedUnitStatuses = useMemo(() => sortOptions(UNIT_STATUSES), []);

    // Items zusätzlich nach Entität filtern (clientseitig, da Backend nur property_id kennt)
    const filteredItems = useMemo(() => {
        if (!entityFilter) return items;
        const allowedPropIds = new Set(properties.filter((p) => p.entity_id === entityFilter).map((p) => p.id));
        return items.filter((u) => allowedPropIds.has(u.property_id));
    }, [items, entityFilter, properties]);

    const sortResolvers = useMemo(() => ({
        unit_label: (u) => (u.unit_label || "").toLowerCase(),
        property: (u) => (propMap[u.property_id]?.label || propMap[u.property_id]?.address || "").toLowerCase(),
        typ: (u) => u.typ || "",
        status: (u) => u.status || "",
        size_sqm: (u) => u.size_sqm ?? null,
    }), [propMap]);

    // Mieter-Index pro Einheit (für Suchfeld)
    const tenantsByUnit = useMemo(() => {
        const m = {};
        tenants.forEach((t) => {
            if (!t.unit_id) return;
            if (!m[t.unit_id]) m[t.unit_id] = [];
            m[t.unit_id].push(t);
        });
        return m;
    }, [tenants]);

    // Volltextsuche
    const searchFields = useMemo(() => [
        "unit_label", "typ", "status", "ausstattung", "kategorie_freitext", "branche", "stellplatz_nummer", "notes",
        (u) => propMap[u.property_id]?.label || "",
        (u) => propMap[u.property_id]?.address || "",
        (u) => propMap[u.property_id]?.plz || "",
        (u) => propMap[u.property_id]?.city || "",
        (u) => entMap[propMap[u.property_id]?.entity_id]?.name || "",
        (u) => u.tracker?.lifecycle_status || "",
        (u) => u.tracker?.lifecycle_strategy || "",
        (u) => (tenantsByUnit[u.id] || []).map((t) => t.full_name).join(" "),
    ], [propMap, entMap, tenantsByUnit]);
    const { query, setQuery, filtered: searched, total, matches, active } = useFulltextFilter(filteredItems, searchFields);

    // Bei aktiver Suche nicht-parent-units inkludieren (sonst verschwinden Zubehör-Treffer)
    const searchedAware = useMemo(
        () => {
            const list = active ? searched : searched.filter((u) => !u.parent_unit_id);
            // Vorsortierung nach Einheit. Array.sort ist stabil, deshalb bleibt
            // diese Ordnung als Zweitsortierung erhalten, wenn danach z. B. nach
            // Immobilie sortiert wird — sonst wäre die Reihenfolge innerhalb
            // eines Objekts die zufällige Ladereihenfolge.
            return [...list].sort((a, b) =>
                (a.unit_label || "").localeCompare(b.unit_label || "", "de", { numeric: true, sensitivity: "base" }),
            );
        },
        [active, searched],
    );
    const { sortKey, sortDir, toggle, sorted: sortedStandalone } = useTableSort(searchedAware, "property", "asc", sortResolvers);
    const unitLabel = (id) => {
        const u = allUnits.find((x) => x.id === id);
        return u ? (u.unit_label || "Einheit") : "";
    };

    const onTypChange = (v) => {
        setForm((f) => ({
            ...f,
            typ: v,
            mwst_pflicht: v === "gewerbe" ? true : false,
            // Reset accessory-only fields when wechsel to Wohnung/Gewerbe
            ...(isHaupt(v) ? { nutzungsart: "", parent_unit_id: "", separat_vermietet: true } : {}),
            // Ein Zimmer traegt immer die Nutzungsart "einzelzimmer" und
            // einen eigenen Vertrag -- es laeuft nie mit der Wohnung mit.
            ...(isZimmer(v) ? { nutzungsart: "einzelzimmer", separat_vermietet: true } : {}),
        }));
    };

    const save = async () => {
        if (!form.property_id) { toast.error("Immobilie auswählen"); return; }
        setSaving(true);
        try {
            const payload = {
                ...form,
                rooms: form.rooms ? parseFloat(form.rooms) : null,
                size_sqm: form.size_sqm ? parseFloat(form.size_sqm) : null,
                parent_unit_id: form.parent_unit_id || null,
                // Nur mitschicken, wenn ein Wert dasteht. Ein Vorgabewert "ganz"
                // wuerde eine zimmerweise vermietete Wohnung beim naechsten
                // Speichern stillschweigend zurueckschalten -- ohne Fehler, ohne
                // Spur, mit Verlust der Zimmervertraege. (Befund des Agenten,
                // 13.10.2026; deshalb ist das Feld im Backend Optional.)
                vermietungsart: form.vermietungsart || undefined,
                // Leere Datumsfelder sind kein Datum: null statt "", sonst
                // steht ein Leerstring in der Datenbank und vergleicht sich
                // mit Faelligkeiten.
                leer_seit: form.leer_seit || null,
                // Mietenden nur beim Wechsel auf "leer", und nur die, die ein
                // Datum tragen -- die Pruefung, ob eines fehlt, macht das Backend
                // und nennt die Betroffenen.
                mietenden: form.status === "leer"
                    ? (form.mietenden || [])
                        .filter((m) => m.mietende)
                        .map((m) => ({ tenant_id: m.tenant_id, mietende: m.mietende,
                                       uebergabe_datum: m.uebergabe_datum || null }))
                    : null,
            };
            if (editingId) {
                await updateUnit(editingId, payload);
                toast.success("Einheit aktualisiert");
            } else {
                await createUnit(payload);
                toast.success("Mieteinheit angelegt");
            }
            setOpen(false); setForm(empty); setEditingId(null); load();
        } catch (e) {
            // Vorher gab es hier kein catch: ein 400 verschwand still, der
            // Dialog blieb offen, und niemand erfuhr, warum nichts passierte.
            const d = e?.response?.data?.detail;
            const titel = typeof d === "string" ? d : d?.error || "Speichern fehlgeschlagen";
            const beschreibung = d?.mieter?.length
                ? `Mietende fehlt für: ${d.mieter.map((m) => m.name).join(", ")}`
                : (typeof d === "object" ? d?.detail : null);
            toast.error(titel, { description: beschreibung });
        } finally { setSaving(false); }
    };

    const startCreate = () => { setEditingId(null); setForm(empty); setOpen(true); };
    const startEdit = (u) => {
        setEditingId(u.id);
        setForm({
            ...empty,
            ...u,
            rooms: u.rooms ?? "",
            size_sqm: u.size_sqm ?? "",
            parent_unit_id: u.parent_unit_id ?? "",
        });
        setOpen(true);
    };

    const parentCandidates = [...allUnits]
        // Haupteinheiten, nicht nur Wohnungen: Die Vermietungsart laesst sich
        // auch an einer Gewerbeflaeche auf "zimmerweise" stellen (zwei
        // Mietparteien auf einer Flaeche), dann braucht auch sie Zimmer.
        .filter((u) => HAUPT_TYPES.includes(u.typ) && (!form.property_id || u.property_id === form.property_id))
        .sort((a, b) => (a.unit_label || "").localeCompare(b.unit_label || "", "de", { sensitivity: "base" }));

    // Wird eine Einheit mit laufenden Mietverhaeltnissen auf "leer" gesetzt,
    // fragt der Dialog je Mietverhaeltnis nach dem Mietende. Bis zum 05.10.2026
    // nahm das System stattdessen das Tagesdatum -- sieben falsche Mietenden in
    // der Achterstr. 33 waren die Folge.
    const laufende = editingId
        ? (tenantsByUnit[editingId] || []).filter((t) => istAktivesMietverhaeltnis(t))
        : [];
    const mietendeVon = (tid) => (form.mietenden || []).find((m) => m.tenant_id === tid) || {};
    const setMietende = (tid, feld, wert) => setForm((f) => {
        const list = [...(f.mietenden || [])];
        const i = list.findIndex((m) => m.tenant_id === tid);
        const m = i >= 0 ? { ...list[i] } : { tenant_id: tid, mietende: "", uebergabe_datum: "" };
        m[feld] = wert;
        if (i >= 0) list[i] = m; else list.push(m);
        return { ...f, mietenden: list };
    });

    return (
        <div className="px-6 md:px-10 py-8 space-y-6">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-gray-500 mb-2">Abrechnungseinheiten</div>
                    <h1 className="font-heading text-3xl font-bold text-slate-900" data-testid="units-title">Mieteinheiten</h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Jede vermietbare Einheit: Wohnungen, Gewerbeflächen, Garagen & Stellplätze. Stellplätze können als Zubehör einer Wohnung geführt werden.
                    </p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button size="sm" className="bg-slate-900 hover:bg-slate-800" data-testid="btn-new-unit"
                                disabled={gesperrt} grund={grund}>
                            <Plus size={14} className="mr-1.5" /> Neue Einheit
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
                        <DialogHeader><DialogTitle>{editingId ? "Mieteinheit bearbeiten" : "Neue Mieteinheit"}</DialogTitle></DialogHeader>
                        <div className="grid grid-cols-2 gap-3 py-2">
                            <div className="col-span-2">
                                <Label>Immobilie *<Hilfe id="einheit-immobilie" /></Label>
                                <SuchAuswahl wert={form.property_id} onWaehlen={(v) => setForm({ ...form, property_id: v })}
                                    optionen={[
                                        ...sortedProperties.map((p) => ({ wert: p.id, text: propertyDisplay(p, lageMap) })),
                                    ]}
                                    platzhalter="Wirtschaftseinheit wählen"
                                    testId="input-unit-property"
                                />
                            </div>
                            <div>
                                <Label>Typ<Hilfe id="einheit-typ" /></Label>
                                <SuchAuswahl wert={form.typ} onWaehlen={onTypChange}
                                    optionen={[
                                        ...sortedUnitTypes.map((u) => ({ wert: u.value, text: u.label })),
                                    ]}
                                    testId="input-unit-typ"
                                />
                            </div>
                            <div>
                                <Label>Status<Hilfe id="einheit-status" /></Label>
                                <SuchAuswahl wert={form.status} onWaehlen={(v) => setForm({ ...form, status: v })}
                                    optionen={[
                                        ...sortedUnitStatuses.map((s) => ({ wert: s.value, text: s.label })),
                                    ]}
                                    testId="input-unit-status"
                                />
                            </div>
                            {form.status === "leer" && laufende.length > 0 && (
                                <div className="col-span-2 border border-amber-200 bg-amber-50 rounded-md p-3 space-y-2"
                                     data-testid="unit-leer-mietenden">
                                    <div className="text-xs text-amber-900">
                                        <span className="font-medium">Diese Einheit hat {laufende.length === 1 ? "ein laufendes Mietverhältnis" : `${laufende.length} laufende Mietverhältnisse`}.</span>{" "}
                                        Trage je Mietverhältnis das Mietende ein (letzter Miettag, meist ein Monatsletzter);
                                        die Übergabe ist optional. Ohne Mietende wird die Einheit nicht auf „leer" gesetzt —
                                        das System erfindet kein Datum.
                                    </div>
                                    {laufende.map((t) => (
                                        <div key={t.id} className="grid grid-cols-3 gap-2 items-end">
                                            <div className="text-sm text-slate-900 truncate pb-2" title={t.full_name}>{t.full_name}</div>
                                            <div>
                                                <Label className="text-xs">Mietende<Hilfe id="mietende" /></Label>
                                                <Input type="date" value={mietendeVon(t.id).mietende || ""}
                                                       onChange={(e) => setMietende(t.id, "mietende", e.target.value)}
                                                       data-testid={`input-mietende-${t.id}`} />
                                            </div>
                                            <div>
                                                <Label className="text-xs">Übergabe (optional)<Hilfe id="uebergabe" /></Label>
                                                <Input type="date" value={mietendeVon(t.id).uebergabe_datum || ""}
                                                       onChange={(e) => setMietende(t.id, "uebergabe_datum", e.target.value)} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {form.status === "leer" && laufende.length === 0 && (
                                <div>
                                    <Label>Leer seit<Hilfe id="leer-seit" /></Label>
                                    <Input type="date" value={form.leer_seit || ""}
                                           onChange={(e) => setForm({ ...form, leer_seit: e.target.value })}
                                           data-testid="input-unit-leer-seit" />
                                </div>
                            )}
                            <div className="col-span-2">
                                <Label>Bezeichnung</Label>
                                <Input value={form.unit_label} onChange={(e) => setForm({ ...form, unit_label: e.target.value })} placeholder="z.B. WE 03 — 2. OG rechts" data-testid="input-unit-label" />
                            </div>

                            {/* Besitz- und Eigentumsdaten traegt nur das Objekt (06.10.2026,
                                O10 = A: das Objekt ist die Erwerbseinheit, die Einheit erbt).
                                Die vier Einheiten-Felder vom 05.10. sind zurueckgebaut. */}

                            {/* Wohnung */}
                            {form.typ === "wohnung" && (
                                <>
                                    <div>
                                        <Label>Zimmer</Label>
                                        <Input type="number" step="0.5" value={form.rooms} onChange={(e) => setForm({ ...form, rooms: e.target.value })} />
                                    </div>
                                    <div>
                                        <Label>Wohnfläche (m²)</Label>
                                        <Input type="number" step="0.1" value={form.size_sqm} onChange={(e) => setForm({ ...form, size_sqm: e.target.value })} />
                                    </div>
                                    <div className="col-span-2">
                                        <Label>Ausstattung</Label>
                                        <Input value={form.ausstattung} onChange={(e) => setForm({ ...form, ausstattung: e.target.value })} placeholder="z.B. Balkon, Einbauküche, Dielen" />
                                    </div>
                                </>
                            )}

                            {/* Gewerbe */}
                            {form.typ === "gewerbe" && (
                                <>
                                    <div>
                                        <Label>Fläche (m²)</Label>
                                        <Input type="number" step="0.1" value={form.size_sqm} onChange={(e) => setForm({ ...form, size_sqm: e.target.value })} />
                                    </div>
                                    <div>
                                        <Label>Branche</Label>
                                        <Input value={form.branche} onChange={(e) => setForm({ ...form, branche: e.target.value })} data-testid="input-unit-branche" placeholder="z.B. Café, Praxis, Büro" />
                                    </div>
                                    <div className="col-span-2 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-md p-3">
                                        <Checkbox id="mwst" checked={form.mwst_pflicht} onCheckedChange={(v) => setForm({ ...form, mwst_pflicht: Boolean(v) })} data-testid="input-unit-mwst" />
                                        <label htmlFor="mwst" className="text-sm">
                                            <span className="font-medium text-amber-900">MwSt-Option aktiviert</span>
                                            <span className="block text-xs text-amber-700 mt-0.5">Mietbescheinigungen enthalten USt-Ausweis · §9 UStG Option zur Steuerpflicht</span>
                                        </label>
                                    </div>
                                </>
                            )}

                            {/* Garage / Stellplatz */}
                            {/* Sonstiges — Freitext-Bezeichnung */}
                            {form.typ === "sonstiges" && (
                                <div className="col-span-2">
                                    <Label>Bezeichnung der Kategorie *</Label>
                                    <Input
                                        value={form.kategorie_freitext}
                                        onChange={(e) => setForm({ ...form, kategorie_freitext: e.target.value })}
                                        placeholder="z.B. Werbefläche Süd, Schornstein, Antennenstandort"
                                        data-testid="input-unit-kategorie"
                                    />
                                </div>
                            )}

                            {/* Wohnung/Gewerbe: als Ganzes oder zimmerweise vermietet.
                                Ein ZUSTAND, keine Erlaubnis -- das Backend weist das
                                Umschalten ab, solange noch ein Vertrag laeuft, und
                                nennt die Namen. Das Feld wird nur mitgeschickt, wenn
                                es sich aendert: Ein Vorgabewert wuerde eine
                                Einzelzimmer-Wohnung beim naechsten Speichern still
                                zurueckschalten. */}
                            {isHaupt(form.typ) && (
                                <div className="col-span-2">
                                    <Label>Vermietungsart<Hilfe id="vermietungsart" /></Label>
                                    <SuchAuswahl
                                        wert={form.vermietungsart || "ganz"}
                                        onWaehlen={(v) => setForm({ ...form, vermietungsart: v })}
                                        optionen={VERMIETUNGSARTEN.map((v) => ({ wert: v.value, text: v.label }))}
                                        testId="input-unit-vermietungsart"
                                    />
                                    {(form.vermietungsart || "ganz") === "einzelzimmer" && (
                                        <p className="text-xs text-gray-500 mt-1">
                                            Die Zimmer werden als eigene Mieteinheiten angelegt (Typ „Zimmer",
                                            übergeordnete Einheit: diese Wohnung). Jedes Zimmer bekommt einen
                                            eigenen Mietvertrag; an der Wohnung selbst hängt dann keiner mehr.
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Garage / Stellplatz: Nummer + Vermietungsmodus */}
                            {(form.typ === "garage" || form.typ === "stellplatz") && (
                                <>
                                    <div>
                                        <Label>Nummer</Label>
                                        <Input value={form.stellplatz_nummer} onChange={(e) => setForm({ ...form, stellplatz_nummer: e.target.value })} data-testid="input-unit-nummer" />
                                    </div>
                                    <div className="flex items-end">
                                        <label className="flex items-center gap-2 text-sm">
                                            <Checkbox checked={form.separat_vermietet} onCheckedChange={(v) => setForm({ ...form, separat_vermietet: Boolean(v) })} data-testid="input-unit-separat" />
                                            Separat vermietet<Hilfe id="separat-vermietet" />
                                        </label>
                                    </div>
                                </>
                            )}

                            {/* Größe für Zubehör-Typen ohne Spezialfeld */}
                            {isZubehoer(form.typ) && form.typ !== "garage" && form.typ !== "stellplatz" && (
                                <div className="col-span-2">
                                    <Label>Fläche / Größe (optional)</Label>
                                    <Input type="number" step="0.1" value={form.size_sqm} onChange={(e) => setForm({ ...form, size_sqm: e.target.value })} placeholder="m² oder Stück" />
                                </div>
                            )}

                            {/* Rechtliche Zugehörigkeit (Grundbuch) — für alle Einheitentypen */}
                            <div className="col-span-2">
                                <Label className="text-sm font-semibold text-slate-900">Rechtliche Zugehörigkeit (Grundbuch)<Hilfe id="eigenes-grundbuch" /></Label>
                                <div className="mt-2 space-y-2">
                                    <label className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${form.eigenes_grundbuch ? "border-slate-900 bg-slate-50" : "border-gray-200 hover:border-gray-300"}`}>
                                        <input
                                            type="radio"
                                            name="grundbuch"
                                            checked={form.eigenes_grundbuch}
                                            onChange={() => setForm({ ...form, eigenes_grundbuch: true, parent_unit_id: "", nutzungsart: "" })}
                                            className="mt-1"
                                            data-testid="grundbuch-eigen"
                                        />
                                        <div>
                                            <div className="text-sm font-medium text-slate-900">Eigenständige Einheit · eigenes Grundbuch (Sondereigentum)</div>
                                            <div className="text-xs text-gray-600 mt-0.5">Eigenes Grundbuchblatt — die Einheit kann allein veräußert werden.</div>
                                        </div>
                                    </label>
                                    <label className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${!form.eigenes_grundbuch ? "border-amber-500 bg-amber-50" : "border-gray-200 hover:border-gray-300"}`}>
                                        <input
                                            type="radio"
                                            name="grundbuch"
                                            checked={!form.eigenes_grundbuch}
                                            onChange={() => setForm({ ...form, eigenes_grundbuch: false, grundbuch_eintrag: "", nutzungsart: isHaupt(form.typ) ? "" : (isZimmer(form.typ) ? "einzelzimmer" : "sondernutzungsrecht") })}
                                            className="mt-1"
                                            data-testid="grundbuch-zubehoer"
                                        />
                                        <div>
                                            {isHaupt(form.typ) ? (
                                                <>
                                                    <div className="text-sm font-medium text-amber-900">Teil eines nicht-aufgeteilten Gebäudes · kein eigenes Grundbuch</div>
                                                    <div className="text-xs text-amber-700 mt-0.5">Das Gebäude ist noch nicht in Sondereigentum aufgeteilt. Diese Einheit ist rechtlich Teil des Gesamtobjekts und kann nicht einzeln veräußert werden.</div>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="text-sm font-medium text-amber-900">Sondernutzungsrecht / Zubehör · kein eigenes Grundbuch</div>
                                                    <div className="text-xs text-amber-700 mt-0.5">Wird zusammen mit einer Wohnung oder dem gesamten Gebäude veräußert (siehe Nutzungsart unten).</div>
                                                </>
                                            )}
                                        </div>
                                    </label>
                                </div>
                            </div>

                            {/* Hinweis: Wohnung/Gewerbe ohne eigenes Grundbuch → Property-Grundbuch ist relevant */}
                            {isHaupt(form.typ) && !form.eigenes_grundbuch && form.property_id && (
                                <div className="col-span-2 bg-blue-50 border border-blue-100 rounded-md p-3 text-xs text-blue-800">
                                    <span className="font-medium">Hinweis:</span> Bei Veräußerung gilt das Grundbuch der gesamten Wirtschaftseinheit „{propMap[form.property_id]?.label || propMap[form.property_id]?.address || "—"}". Der Grundbuch-Eintrag ist dort hinterlegt.
                                </div>
                            )}

                            {/* Nutzungsart-Auswahl: nur für Zubehör-Typen ohne eigenes Grundbuch */}
                            {isZubehoer(form.typ) && !form.eigenes_grundbuch && (
                                <div className="col-span-2">
                                    <Label className="text-sm font-semibold text-slate-900">Nutzungsart</Label>
                                    <div className="mt-2 space-y-2">
                                        <label className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${form.nutzungsart === "sondernutzungsrecht" ? "border-slate-900 bg-slate-50" : "border-gray-200 hover:border-gray-300"}`}>
                                            <input type="radio" name="nutzungsart" checked={form.nutzungsart === "sondernutzungsrecht"} onChange={() => setForm({ ...form, nutzungsart: "sondernutzungsrecht" })} className="mt-1" data-testid="nutzung-snr" />
                                            <div>
                                                <div className="text-sm font-medium text-slate-900">Sondernutzungsrecht — gehört zu einer Wohnung</div>
                                                <div className="text-xs text-gray-600 mt-0.5">In aufgeteilten Objekten: ausschließliches Nutzungsrecht der Eigentümergemeinschaft an einem Teil des Gemeinschaftseigentums.</div>
                                            </div>
                                        </label>
                                        <label className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${form.nutzungsart === "zubehoer" ? "border-slate-900 bg-slate-50" : "border-gray-200 hover:border-gray-300"}`}>
                                            <input type="radio" name="nutzungsart" checked={form.nutzungsart === "zubehoer"} onChange={() => setForm({ ...form, nutzungsart: "zubehoer" })} className="mt-1" data-testid="nutzung-zubehoer" />
                                            <div>
                                                <div className="text-sm font-medium text-slate-900">Zubehör — gehört zu einer Wohnung</div>
                                                <div className="text-xs text-gray-600 mt-0.5">Untrennbar an eine bestimmte Wohnung gebunden.</div>
                                            </div>
                                        </label>
                                        <label className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${form.nutzungsart === "gesamtobjekt" ? "border-slate-900 bg-slate-50" : "border-gray-200 hover:border-gray-300"}`}>
                                            <input type="radio" name="nutzungsart" checked={form.nutzungsart === "gesamtobjekt"} onChange={() => setForm({ ...form, nutzungsart: "gesamtobjekt", parent_unit_id: "" })} className="mt-1" data-testid="nutzung-gesamt" />
                                            <div>
                                                <div className="text-sm font-medium text-slate-900">Gesamtobjekt — gehört zum nicht-aufgeteilten Gebäude</div>
                                                <div className="text-xs text-gray-600 mt-0.5">Bei nicht-aufgeteilten MFH: gehört zum Gebäude als Ganzes (z.B. gemeinschaftlicher Hinterhof-Garten).</div>
                                            </div>
                                        </label>
                                    </div>
                                </div>
                            )}

                            {/* Übergeordnete Einheit — bei Zubehör (SNR/Zubehör) und bei Zimmern.
                                Ohne den Zimmer-Zweig liesse sich ein Zimmer nicht an seine
                                Wohnung haengen: isZubehoer schliesst Zimmer seit dem
                                13.10.2026 aus, und ihre Nutzungsart heisst "einzelzimmer". */}
                            {(isZubehoer(form.typ) || isZimmer(form.typ)) && !form.eigenes_grundbuch
                                && (form.nutzungsart === "sondernutzungsrecht" || form.nutzungsart === "zubehoer"
                                    || form.nutzungsart === "einzelzimmer") && (
                                <div className="col-span-2">
                                    <Label>{isZimmer(form.typ) ? "Wohnung, zu der das Zimmer gehört *" : "Parent-Wohnung *"}<Hilfe id="uebergeordnete-einheit" /></Label>
                                    <SuchAuswahl wert={form.parent_unit_id || ""} onWaehlen={(v) => setForm({ ...form, parent_unit_id: v })}
                                        optionen={[
                                            ...parentCandidates.map((u) => ({ wert: u.id, text: `${u.unit_label || "Wohnung"} · ${propMap[u.property_id]?.label || ""}` })),
                                        ]}
                                        platzhalter="— Wohnung wählen —"
                                        testId="input-unit-parent"
                                    />
                                    <div className="text-[11px] text-gray-500 mt-1">Beim Übergabeprotokoll dieser Wohnung wird die Zubehör-Einheit automatisch mitgeführt.</div>
                                </div>
                            )}

                            {/* Grundbuch-Eintrag — nur wenn eigenes Grundbuch */}
                            {form.eigenes_grundbuch && (
                                <div className="col-span-2">
                                    <Label>Grundbuch-Eintrag</Label>
                                    <Textarea
                                        rows={2}
                                        value={form.grundbuch_eintrag}
                                        onChange={(e) => setForm({ ...form, grundbuch_eintrag: e.target.value })}
                                        placeholder="z.B. Grundbuch von Aachen, Blatt 12345, Flur 28, Flurstück 758"
                                        data-testid="input-unit-grundbuch"
                                    />
                                    <div className="text-[11px] text-gray-500 mt-1">Frei formuliertes Grundbuch-Aktenzeichen — relevant für Veräußerung & Notarvorgänge.</div>
                                </div>
                            )}

                            {/* IBAN-Override für Einheit */}
                            <div className="col-span-2">
                                <Label>Eigene IBAN für diese Einheit (optional)<Hilfe id="einheit-iban" /></Label>
                                <Input
                                    value={form.iban || ""}
                                    onChange={(e) => setForm({ ...form, iban: e.target.value })}
                                    placeholder={(() => {
                                        const p = propMap[form.property_id];
                                        const e = p ? entMap[p.entity_id] : null;
                                        const inherited = p?.iban || e?.iban;
                                        return inherited ? `Geerbt: ${inherited}` : "Wenn leer: IBAN der Immobilie / Entität wird verwendet";
                                    })()}
                                    data-testid="input-unit-iban"
                                />
                                <div className="text-[11px] text-gray-500 mt-1">Überschreibt die geerbte IBAN aus Immobilie / Entität für Mieteingänge dieser Einheit.</div>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
                            <Button onClick={save} disabled={saving || gesperrt} grund={sperre([saving, "Wird gespeichert"], [gesperrt, grund])} className="bg-slate-900 hover:bg-slate-800" data-testid="btn-save-unit">
                                {saving ? "Speichere…" : (editingId ? "Aktualisieren" : "Speichern")}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Filters */}
            <div className="bg-white border border-gray-200 rounded-md p-3 flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <Label className="text-xs text-gray-500">Entität</Label>
                    <Select
                        value={entityFilter || "all"}
                        onValueChange={(v) => {
                            // Entitäts-Wechsel → property_id-Filter verwerfen
                            const next = {};
                            if (v !== "all") next.entity_id = v;
                            setSearchParams(next);
                        }}
                    >
                        <SelectTrigger className="h-8 w-[240px]" data-testid="filter-entity"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Alle Entitäten</SelectItem>
                            {sortedEntities.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div className="flex items-center gap-2">
                    <Label className="text-xs text-gray-500">Immobilie</Label>
                    <Select
                        value={propertyFilter || "all"}
                        onValueChange={(v) => {
                            const next = {};
                            if (entityFilter) next.entity_id = entityFilter;
                            if (v !== "all") next.property_id = v;
                            setSearchParams(next);
                        }}
                    >
                        <SelectTrigger className="h-8 w-[260px]" data-testid="filter-property"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">{entityFilter ? "Alle Immobilien dieser Entität" : "Alle Immobilien"}</SelectItem>
                            {sortedProperties.map((p) => <SelectItem key={p.id} value={p.id}>{propertyDisplay(p, lageMap)}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div className="flex items-center gap-2">
                    <Label className="text-xs text-gray-500">Typ</Label>
                    <SuchAuswahl wert={typFilter || "all"} onWaehlen={(v) => setTypFilter(v === "all" ? "" : v)}
                        optionen={[
                            { wert: "all", text: "Alle Typen" },
                            ...sortedUnitTypes.map((t) => ({ wert: t.value, text: t.label })),
                        ]}
                        className="w-[160px]"
                        testId="filter-typ"
                    />
                </div>
                <div className="flex items-center gap-2">
                    <Label className="text-xs text-gray-500">Status</Label>
                    <SuchAuswahl wert={statusFilter || "all"} onWaehlen={(v) => setStatusFilter(v === "all" ? "" : v)}
                        optionen={[
                            { wert: "all", text: "Alle Status" },
                            ...sortedUnitStatuses.map((s) => ({ wert: s.value, text: s.label })),
                        ]}
                        className="w-[160px]"
                        testId="filter-status"
                    />
                </div>
                <div className="flex items-center gap-2">
                    <Label className="text-xs text-gray-500">Strategie</Label>
                    <SuchAuswahl wert={strategyFilter || "all"} onWaehlen={(v) => setStrategyFilter(v === "all" ? "" : v)}
                        optionen={[
                            { wert: "all", text: "Alle Strategien" },
                            ...LIFECYCLE_STRATEGIES.map((s) => ({ wert: s.value, text: s.label })),
                        ]}
                        className="w-[160px]"
                        testId="filter-strategy"
                    />
                </div>
                <div className="flex items-center gap-2">
                    <Label className="text-xs text-gray-500">Lifecycle</Label>
                    <SuchAuswahl wert={lifecycleStatusFilter || "all"} onWaehlen={(v) => setLifecycleStatusFilter(v === "all" ? "" : v)}
                        optionen={[
                            { wert: "all", text: "Alle Lifecycle-Stati" },
                            ...sortOptions(LIFECYCLE_STATUSES).map((s) => ({ wert: s.value, text: s.label })),
                        ]}
                        className="w-[180px]"
                        testId="filter-lifecycle-status"
                    />
                </div>
                <div className="ml-auto flex items-center gap-3">
                    <SearchBox value={query} onChange={setQuery} placeholder="Suche Adresse, Einheit, Mieter…" testid="units-search" className="w-[280px]" />
                    <MatchCount matches={matches} total={total} label="Einheiten" />
                </div>
            </div>

            {/* overflow-x-auto, nicht overflow-hidden: die Tabelle ist breiter als der
                Bildschirm, sobald genug Spalten da sind — mit "hidden" verschwinden die
                rechten Spalten lautlos und sind auf keinem Weg erreichbar. Gemessen am
                10.09.2026 bei 1280 px: Mieter 210 px abgeschnitten, Einheiten 183 px. */}
            <div className="bg-white border border-gray-200 rounded-md overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-[11px] uppercase tracking-[0.12em] text-gray-500">
                        <tr>
                            <SortHeader label="Immobilie · Entität" sortKey="property" state={{sortKey, sortDir}} onToggle={toggle} />
                            <SortHeader label="Einheit" sortKey="unit_label" state={{sortKey, sortDir}} onToggle={toggle} />
                            <SortHeader label="Typ" sortKey="typ" state={{sortKey, sortDir}} onToggle={toggle} />
                            <SortHeader label="Status" sortKey="status" state={{sortKey, sortDir}} onToggle={toggle} />
                            <th className="text-left font-medium px-4 py-3">Lifecycle</th>
                            <SortHeader label="Fläche" sortKey="size_sqm" state={{sortKey, sortDir}} onToggle={toggle} />
                            {/* Statt "Spezifisch" (Ausstattung/Branche, im Bestand fast
                                ueberall leer) steht hier das Grundbuchblatt. Gefuellt
                                heisst: eigenes Grundbuch. Leer heisst: keines. */}
                            <th className="text-left font-medium px-4 py-3">Grundbuchblatt<Hilfe id="grundbuchblatt" /></th>
                            <th className="text-left font-medium px-4 py-3">Aktueller Mieter</th>
                            <th className="text-right font-medium px-4 py-3">Kaltmiete<Hilfe id="kaltmiete-heute" /></th>
                            <th className="text-right font-medium px-4 py-3">NK-Vorausz.<Hilfe id="nk-vorauszahlung" /></th>
                            <th className="text-right font-medium px-4 py-3 sticky right-0 bg-gray-50 shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.15)]"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {(() => {
                            // Sortiere: Hauptobjekte (bereits sortiert) zuerst, danach ihre Zubehöreinheiten direkt darunter
                            const standalone = sortedStandalone;
                            const accByParent = {};
                            items.filter((u) => u.parent_unit_id).forEach((u) => {
                                if (!accByParent[u.parent_unit_id]) accByParent[u.parent_unit_id] = [];
                                accByParent[u.parent_unit_id].push(u);
                            });
                            // Auch Zubehöreinheiten anzeigen, deren Parent nicht im Filter-Ergebnis ist
                            const visibleParentIds = new Set(standalone.map((u) => u.id));
                            const orphanAcc = items.filter((u) => u.parent_unit_id && !visibleParentIds.has(u.parent_unit_id));
                            const rows = [];
                            standalone.forEach((u) => {
                                rows.push({ unit: u, accessory: false });
                                (accByParent[u.id] || []).forEach((a) => rows.push({ unit: a, accessory: true }));
                            });
                            orphanAcc.forEach((a) => rows.push({ unit: a, accessory: true, orphan: true }));
                            return rows;
                        })().map(({ unit: u, accessory, orphan }) => {
                            const p = propMap[u.property_id];
                            const e = p ? entMap[p.entity_id] : null;
                            const accCount = items.filter((x) => x.parent_unit_id === u.id).length;
                            const expanded = expandedTasks[u.id];
                            return (
                                <React.Fragment key={u.id}>
                                <tr className={`group hover:bg-gray-50 ${accessory ? "bg-amber-50/30" : ""}`} data-testid={`unit-row-${u.id}`}>
                                    <td className="px-4 py-3 text-gray-700">
                                        {p ? (
                                            <Link to={`/immobilien/${p.id}`} className="text-xs hover:underline"
                                                  data-testid={`unit-property-link-${u.id}`} title="Immobilie öffnen">
                                                {propertyDisplay(p, lageMap)}
                                            </Link>
                                        ) : <span className="text-xs">—</span>}
                                        <div className="text-[11px] text-gray-400">{e?.name || ""}</div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            {accessory && (
                                                <span className="inline-flex items-center text-amber-700 text-xs ml-2">
                                                    └─
                                                </span>
                                            )}
                                            <Home size={14} className={accessory ? "text-amber-500" : "text-gray-400"} />
                                            <div className="min-w-0">
                                                <div className="font-medium text-slate-900 flex items-center gap-2">
                                                    <Link to={`/einheiten/${u.id}`} className="hover:underline" data-testid={`unit-link-${u.id}`}>
                                                        {u.unit_label || "—"}
                                                    </Link>
                                                    {/* Die Marke "kein eigenes Grundbuch" ist weg: was fehlt,
                                                        muss nicht beschriftet werden. Die Spalte Grundbuchblatt
                                                        bleibt dann einfach leer. */}
                                                    {accCount > 0 && !accessory && (
                                                        <span className="text-[10px] bg-slate-100 text-slate-700 border border-slate-200 rounded px-1.5 py-0.5 font-normal">
                                                            +{accCount} Zubehör
                                                        </span>
                                                    )}
                                                </div>
                                                {u.parent_unit_id && (
                                                    <div className="text-[10px] text-amber-700">
                                                        Zubehör zu {unitLabel(u.parent_unit_id)}
                                                        {orphan && <span className="text-gray-400"> (Parent außerhalb Filter)</span>}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-gray-700 text-xs">
                                        {u.typ === "sonstiges" ? (u.kategorie_freitext || "—") : statusLabel(u.typ)}
                                        {u.typ === "gewerbe" && u.mwst_pflicht && (
                                            <span className="ml-1.5 text-[10px] bg-amber-50 text-amber-700 border border-amber-200 rounded px-1">+MwSt</span>
                                        )}
                                        {u.nutzungsart && (
                                            <div className="text-[10px] text-slate-500 mt-0.5">{statusLabel(u.nutzungsart)}</div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3"><StatusBadge status={u.status} /></td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-1.5">
                                            <LifecycleBadge unitId={u.id} tracker={u.tracker} onChanged={load} compact />
                                            {(() => {
                                                const tasks = u.tracker?.open_tasks || [];
                                                const open = tasks.filter((t) => !t.is_completed);
                                                const today = new Date().toISOString().slice(0, 10);
                                                const overdue = open.filter((t) => t.due_date && t.due_date < today).length;
                                                if (tasks.length === 0) {
                                                    return (
                                                        <button
                                                            type="button"
                                                            className="text-[10px] text-gray-300 hover:text-slate-700"
                                                            onClick={() => setExpandedTasks((s) => ({ ...s, [u.id]: !s[u.id] }))}
                                                            data-testid={`expand-tasks-${u.id}`}
                                                        >
                                                            + Aufgabe
                                                        </button>
                                                    );
                                                }
                                                return (
                                                    <button
                                                        type="button"
                                                        onClick={() => setExpandedTasks((s) => ({ ...s, [u.id]: !s[u.id] }))}
                                                        className={`text-[10px] px-1.5 py-0.5 rounded ${overdue > 0
                                                            ? "bg-red-100 text-red-700 hover:bg-red-200"
                                                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                                                        data-testid={`expand-tasks-${u.id}`}
                                                        title={`${open.length} offen${overdue > 0 ? `, davon ${overdue} überfällig` : ""}`}
                                                    >
                                                        📋 {open.length}{overdue > 0 ? ` ⚠${overdue}` : ""}
                                                    </button>
                                                );
                                            })()}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-gray-500 tabular-nums text-xs">{u.size_sqm ? `${u.size_sqm} m²` : "—"}{u.rooms ? ` · ${u.rooms} Zi.` : ""}</td>
                                    {/* Grundbuchblatt. Gefuellt = eigenes Grundbuch, leer = keines
                                        (frueher stand es andersherum als gelbe Marke am Namen).
                                        Eigenes Grundbuch ohne erfasstes Blatt bekommt einen
                                        Strich -- sonst waere es von "kein eigenes" nicht zu
                                        unterscheiden. */}
                                    <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">
                                        {u.eigenes_grundbuch ? (
                                            u.grundbuch_eintrag ? (
                                                <span title={u.grundbuch_eintrag}>{grundbuchblatt(u.grundbuch_eintrag)}</span>
                                            ) : (
                                                <span className="text-gray-300">—</span>
                                            )
                                        ) : ""}
                                    </td>
                                    <td className="px-4 py-3 text-gray-700 text-xs">
                                        {(() => {
                                            const ts = tenantsByUnit[u.id] || [];
                                            // Auszugsdatum schlaegt den Status: wer ausgezogen ist,
                                            // ist kein aktueller Mieter mehr.
                                            const cur = ts.find((t) => istAktivesMietverhaeltnis(t) && t.status === "aktiv")
                                                || ts.find((t) => istAktivesMietverhaeltnis(t));
                                            if (!cur) return <span className="text-gray-400">—</span>;
                                            return (
                                                <Link to={`/mieter?edit=${cur.id}`} className="hover:underline"
                                                      data-testid={`unit-tenant-${u.id}`} title="Mietverhältnis öffnen">
                                                    {cur.full_name}
                                                    {cur.status === "gekuendigt" && (
                                                        <span className="ml-1.5 text-[10px] bg-amber-50 text-amber-700 border border-amber-200 rounded px-1">gekündigt</span>
                                                    )}
                                                </Link>
                                            );
                                        })()}
                                    </td>
                                    {/* Miete und Vorauszahlung stehen am Mietverhaeltnis, nicht an
                                        der Einheit: ohne Mieter bleiben beide leer. Bei Pauschalmiete
                                        gibt es keine getrennte Vorauszahlung -- sie steckt drin. */}
                                    {(() => {
                                        const ts = tenantsByUnit[u.id] || [];
                                        const cur = ts.find((t) => istAktivesMietverhaeltnis(t) && t.status === "aktiv")
                                            || ts.find((t) => istAktivesMietverhaeltnis(t));
                                        const angepasst = cur ? mieten[cur.id] : null;
                                        const pauschal = cur?.mietart === "pauschal";
                                        const betrag = !cur ? null
                                            : pauschal ? (angepasst?.pauschalmiete ?? cur.pauschalmiete)
                                            : (angepasst?.kaltmiete ?? cur.kaltmiete);
                                        return (
                                            <>
                                                <td className="px-4 py-3 text-right text-gray-700 text-xs tabular-nums whitespace-nowrap">
                                                    {betrag == null ? <span className="text-gray-400">—</span> : euro(betrag)}
                                                    {pauschal && <div className="text-[10px] text-gray-400">pauschal</div>}
                                                    {angepasst && (
                                                        <div className="text-[10px] text-emerald-700"
                                                             title={`Im Mietverhältnis stehen ${euro(pauschal ? cur.pauschalmiete : cur.kaltmiete)}.`}>
                                                            ab {angepasst.gilt_ab}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-right text-gray-700 text-xs tabular-nums whitespace-nowrap">
                                                    {!cur ? <span className="text-gray-400">—</span>
                                                        : pauschal ? <span className="text-gray-400">in Pauschale</span>
                                                        : cur.nebenkosten != null ? euro(cur.nebenkosten)
                                                        : <span className="text-gray-400">—</span>}
                                                    {!pauschal && cur?.heizkosten ? (
                                                        <div className="text-[10px] text-gray-400" title="Heizkosten-Vorauszahlung">
                                                            + {euro(cur.heizkosten)} Heiz.
                                                        </div>
                                                    ) : null}
                                                </td>
                                            </>
                                        );
                                    })()}
                                    <td className={`px-4 py-3 text-right whitespace-nowrap sticky right-0 group-hover:bg-gray-50 shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.15)] ${accessory ? "bg-[#fffdf5]" : "bg-white"}`}>
                                        <Link to={`/immobilien/${u.property_id}?tab=wiki`} data-testid={`wiki-unit-${u.id}`} title="Wiki der Immobilie öffnen"
                                              aria-label="Wiki der Immobilie öffnen">
                                            <Button variant="ghost" size="sm" className="h-8">
                                                <BookOpen size={12} />
                                            </Button>
                                        </Link>
                                        <Button variant="ghost" size="sm" className="h-8" onClick={() => startEdit(u)} data-testid={`edit-unit-${u.id}`}
                                                disabled={gesperrt} grund={grund}>
                                            <Pencil size={12} />
                                        </Button>
                                        <DeleteConfirm
                                            label={u.unit_label || u.id.slice(0, 8)}
                                            kind="unit"
                                            onDelete={(cascade) => deleteUnit(u.id, cascade)}
                                            onDeleted={load}
                                            testid={`delete-unit-${u.id}`}
                                            disabled={gesperrt}
                                            grund={grund}
                                        />
                                    </td>
                                </tr>
                                {expanded && (
                                    <tr key={`${u.id}-tasks`} className={accessory ? "bg-amber-50/20" : "bg-slate-50/30"}>
                                        <td colSpan={11} className="px-4 py-3">
                                            <TaskList
                                                unitId={u.id}
                                                tasks={u.tracker?.open_tasks || []}
                                                onChanged={load}
                                            />
                                        </td>
                                    </tr>
                                )}
                                </React.Fragment>
                            );
                        })}
                        {items.length === 0 && (
                            <tr><td colSpan={11} className="px-4 py-10 text-center text-gray-400 text-sm">Keine Einheiten gefunden — Filter anpassen oder neu anlegen.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default Units;
